"use client";

import { Button } from "@universe/ui/components/button";
import { Skeleton } from "@universe/ui/components/skeleton";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { ReceiptText, RefreshCcw } from "lucide-react";
import dynamic from "next/dynamic";
import { useParams, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { type DateRange } from "react-day-picker";

import { DatePickerRange } from "@/components/date-picker-range";
import StickyHeader from "@/components/sticky-header";
import { DATE_PARAM_FORMAT } from "@/constants";
import { Route } from "@/enums/route";
import { useActiveAccounts } from "@/hooks/useActiveAccounts";
import { useMeasuredHeight } from "@/hooks/useMeasuredHeight";
import { useDashboardHeader, useLedgerEntryList } from "@/providers";

// The statement dialog (and the PDF/CSV export code behind it) is kept out of
// the dashboard's first-load JS. `loading` renders nothing: the dialog is
// portalled, so an inline fallback would only shift this header's flex row
// (measured by e2e/ledger-header-sticky.spec.ts).
const GenerateStatement = dynamic(
  () => import("@/components/generate-statement"),
  { ssr: false, loading: () => null }
);

// Same specifier as the `dynamic` loader above, so both resolve one chunk.
// A warm-up that fails (offline, a deploy swapped the chunk) is not an error
// the user sees: the click loads the chunk again through `dynamic`, and a
// failure there reaches the error boundary. Warn so it stays visible.
const warmGenerateStatement = () => {
  import("@/components/generate-statement").catch((error: unknown) => {
    console.warn("Statement dialog warm-up failed", error);
  });
};

export default function LedgerEntryHeader() {
  const tLedger = useTranslations("ledger");
  const isMobile = useIsMobile();
  const params = useParams();
  const productId = params.productId as string;
  const { accounts: activeAccounts } = useActiveAccounts();
  const { listHeaderTop, setListHeaderHeight } = useDashboardHeader();

  // Report this header's real height so the date-group headers pin directly
  // below it — and follow it when the filter row hides (empty ledger) or wraps
  // on mobile.
  const headerRef = useMeasuredHeight(setListHeaderHeight);

  const [isGenerateStatementOpen, setIsGenerateStatementOpen] = useState(false);
  // Latched on the first open and never cleared, so the dialog stays mounted
  // and its close animation still runs.
  const [hasOpenedStatement, setHasOpenedStatement] = useState(false);

  const pathname = usePathname();
  const isDashboardPage = pathname === Route.Dashboard;

  const {
    isLoading,
    isFetching,
    isInitialLoad,
    isEmpty,
    refresh,
    setFilters,
    resetFilters,
  } = useLedgerEntryList();

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const applyFilters = (range: DateRange | undefined) => {
    setSelectedDateRange(
      range || {
        from: undefined,
        to: undefined,
      }
    );
    setFilters({
      startDate: range?.from
        ? format(new Date(range.from), DATE_PARAM_FORMAT)
        : undefined,
      endDate: range?.to
        ? format(new Date(range.to), DATE_PARAM_FORMAT)
        : undefined,
    });
  };

  const isLoadingOrFetching = isLoading || isFetching || isInitialLoad;

  const hasDateFilter = !!selectedDateRange.from || !!selectedDateRange.to;

  // Three rendering states for the filter row:
  // - showSkeleton: initial load, no data yet — render placeholders so layout doesn't shift
  // - showFilter: real picker visible
  // - (else): hide entirely (empty ledger, no filter applied, not loading)
  // We use `isInitialLoad` (not `isLoadingOrFetching`) so background re-fetches don't
  // swap a real picker for a skeleton once data has been seen.
  //
  // CRITICAL: gate the skeleton on `!hasDateFilter`. Applying a filter calls
  // `setFilters`, which flips `isInitialLoad` back to true while the filtered
  // page reloads. Without this guard the live <DatePickerRange> would unmount
  // mid-interaction; it then remounts with a fresh `hasInteracted=false` while
  // the ?fromDate/?toDate URL params persist, so its mount effect re-fires
  // `onDateRangeChange` → re-applies the filter → unmounts again → infinite
  // "Maximum update depth exceeded" loop. Keeping the picker mounted once a
  // filter is active breaks the cycle. See .claude/rules/web/render-stability.md.
  const showSkeleton = isInitialLoad && !hasDateFilter;
  const showFilter = !showSkeleton && (!isEmpty || hasDateFilter);

  // Check if the current productId is in activeAccounts
  const isActiveAccount = productId
    ? activeAccounts.some((a) => a.code === productId)
    : false;

  // On an account page the ledger is scoped to a single product, so the button
  // names it ("Generate EUR statement"). `productId` only exists on
  // /accounts/[productId] and is the canonical account code — every link there
  // is built from `account.code`. The dashboard aggregates every account, so it
  // keeps the generic label.
  const statementLabel = productId
    ? tLedger(isMobile ? "statementMobileProduct" : "statementDesktopProduct", {
        product: productId,
      })
    : tLedger(isMobile ? "statementMobile" : "statementDesktop");

  // Helper function to disable dates after today
  const disableFutureDates = (date: Date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of day for comparison
    return date > today;
  };

  return (
    <StickyHeader
      ref={headerRef}
      subHeader={3}
      noPageHeader
      className="flex flex-col gap-4 lg:gap-3 justify-between pt-[3px] lg:pt-[2px]"
      style={{
        top: `calc(${listHeaderTop}px + var(--s21-banner-height, 0px))`,
      }}
    >
      {/* Below md the refresh button, the statement button, the date-range
          trigger and its clear button get a 44x44 CSS px tap target; the
          390x844 describe of e2e/ledger-header-sticky.spec.ts measures all
          four, and the band's own horizontal overflow. md and up keep the
          shared Button sizes. The title row's gaps tighten below md so the
          larger refresh button still leaves room for the title on one line
          at a 375px viewport. */}
      <div className="flex items-center justify-between gap-4 max-md:gap-3">
        <div className="flex items-center gap-3 max-md:gap-2">
          <h2 className="text-lg md:text-xl font-semibold">
            {tLedger("title")}
          </h2>
          {/* Never invalidate `LedgerEntries` directly here: `refresh` resets
              the cursor first, or the last loaded page is appended again. It
              must not run while a ledger request is in flight, hence the
              `isFetching` gate (see LedgerEntryListProvider).
              The gate is `aria-disabled` plus an early return, not native
              `disabled`: a natively disabled button drops keyboard focus
              each time a ledger page starts loading. The `aria-disabled:`
              classes repeat the shared Button's `disabled:` look. Focus
              retention and the ignored Enter while loading are measured by
              "refresh button keeps keyboard focus and ignores Enter while a
              ledger page is loading" in e2e/ledger-refresh-pagination.spec.ts. */}
          <Button
            variant="outline"
            size="icon"
            className="max-md:size-11 aria-disabled:pointer-events-none aria-disabled:bg-muted aria-disabled:text-muted-foreground aria-disabled:opacity-60"
            aria-label={tLedger("refresh")}
            aria-disabled={isFetching}
            onClick={(e) => {
              e.preventDefault();
              if (isFetching) {
                return;
              }
              refresh();
            }}
          >
            <div className="relative size-4">
              <AnimatePresence mode="wait">
                {!isLoadingOrFetching && (
                  <motion.div
                    key="static"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    className="absolute inset-0"
                  >
                    <RefreshCcw className="size-4" />
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isLoadingOrFetching ? 1 : 0 }}
                transition={{ duration: 0.1 }}
                className="absolute inset-0"
              >
                <RefreshCcw className="size-4 animate-spin-reverse" />
              </motion.div>
            </div>
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Button
            className="max-md:h-11"
            // Start fetching the dialog's chunk on intent, so a click rarely
            // waits on the network.
            onPointerEnter={warmGenerateStatement}
            onFocus={warmGenerateStatement}
            onClick={() => {
              setHasOpenedStatement(true);
              setIsGenerateStatementOpen(true);
            }}
            disabled={!isActiveAccount && !isDashboardPage}
          >
            <ReceiptText />
            <span>{statementLabel}</span>
          </Button>
          {hasOpenedStatement && (
            <GenerateStatement
              isOpen={isGenerateStatementOpen}
              onOpenChange={setIsGenerateStatementOpen}
            />
          )}
        </div>
      </div>

      {showSkeleton && (
        <div className="flex flex-col gap-3 lg:gap-5 max-w-[600px]">
          <Skeleton className="h-4 w-32" />
          {/* Must match the taller trigger below md so the swap does not shift the rows. */}
          <Skeleton className="h-9 max-md:h-11 w-full" />
        </div>
      )}
      {showFilter && (
        <>
          <div className="hidden md:flex">
            <DatePickerRange
              className="max-w-[600px]"
              label={tLedger("datePickerLabel")}
              placeholder={tLedger("datePickerPlaceholder")}
              selected={selectedDateRange}
              mode="range"
              numberOfMonths={2}
              onDateRangeChange={applyFilters}
              onClearDateRange={resetFilters}
              disabledDates={disableFutureDates}
            />
          </div>
          <div className="flex md:hidden">
            <DatePickerRange
              label={tLedger("datePickerLabel")}
              placeholder={tLedger("datePickerPlaceholder")}
              selected={selectedDateRange}
              mode="range"
              numberOfMonths={1}
              triggerClassName="max-md:h-11"
              clearButtonClassName="max-md:size-11"
              onDateRangeChange={applyFilters}
              onClearDateRange={resetFilters}
              disabledDates={disableFutureDates}
            />
          </div>
        </>
      )}
    </StickyHeader>
  );
}
